import {
  SlideAction,
  SlideLayout,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useCurrentWorkspace } from 'libs/shared-logic/src/auth/hooks/useCurrentWorkspace';
import { useCallback, useRef, useState } from 'react';
import { View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { NewIssueBody } from '../../issues';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import { useWindowDimensions } from '../../shared/hooks/useWindowDimensions';
import ActivityEditLabels from './activity-edit-label';
import { ActivityFilters } from './activityFilters';
import { ActivityFoldOneColumn } from './activityFoldOneColumn';

export const ActivityNew = () => {
  const navigate = useNavigate();
  const windowDimensions = useWindowDimensions();
  const windowHeight = windowDimensions?.height ?? 0;
  const [selectedLabels, setSelectedLabels] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [isLabelsPanelOpen, setIsLabelsPanelOpen] = useState(false);
  const currentWorkspace = useCurrentWorkspace();
  const resetLabelsRef = useRef<(() => void) | undefined>(undefined);

  const maxHeight = windowHeight - 100;

  const onIssueSelected = useCallback(
    (issueId: string) => {
      navigate(`/activities/${issueId}`);
    },
    [navigate]
  );
  
  const handleFilterChange = useCallback(() => {
    navigate('/activities/filters', { state: { skipAutoSelect: true } });
  }, [navigate]);

  const leftColumn = {
    title: 'activity.menu.title',
    mainComponent: (
      <ActivityLeftColumn
        onIssueSelected={onIssueSelected}
        showAssetName={true}
        maxHeight={maxHeight}
        hideFilterBar={true}
      />
    ),
    headerComponent: <ActivityFilters onFilterChange={handleFilterChange} />,
    disableScroll: true,
  };
      
  const rightColumn = {
    title: 'issue.new.addIt',
    mainComponent: (
      <NewIssueBody
        workspaceId={currentWorkspace?.id}
        isOpened={true}
        modalType="sheet"
        onComplete={() => navigate('/activities')}
        onCancel={() => navigate('/activities')}
        onIssueCreated={(issueId) => navigate(`/activities/${issueId}`)}
        selectedLabels={selectedLabels}
        onLabelsChange={setSelectedLabels}
        onSelectorEdit={() => setIsLabelsPanelOpen(true)}
      />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.SINGLE,
    onBackButtonPress: () => {
      navigate('/activities', { state: { skipAutoSelect: true } });
    },
    disableScroll: true,
  };

  const thirdColumn = isLabelsPanelOpen
    ? {
        title: 'asset.groups',
        mainComponent: (
          <ActivityEditLabels
            selectedLabels={selectedLabels ?? []}
            onLabelsChange={setSelectedLabels}
            resetRef={resetLabelsRef}
          />
        ),
        headerComponent: (
          <View>
            <HeaderResetButton onReset={() => resetLabelsRef.current?.()} />
          </View>
        ),
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setIsLabelsPanelOpen(false),
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
    />
  );
};

const ActivityLeftColumn = ({
  onIssueSelected,
  showAssetName,
  selectedIssue,
  maxHeight,
  hideFilterBar,
}: {
  onIssueSelected: (issueId: string) => void;
  showAssetName: boolean;
  selectedIssue?: string;
  maxHeight: number;
  hideFilterBar: boolean;
}) => {
  const { onSlideChange, currentViewMode } = useSlideLayout();

  const handleIssueSelected = useCallback(
    (issueId: string) => {
      if (currentViewMode !== ViewMode.DUAL) {
        onSlideChange(SlideAction.DUAL);
      }
      onIssueSelected(issueId);
    },
    [onSlideChange, currentViewMode, onIssueSelected]
  );

  return (
    <ActivityFoldOneColumn
      onIssueSelected={handleIssueSelected}
      showAssetName={showAssetName}
      selectedIssue={selectedIssue}
      maxHeight={maxHeight}
      hideFilterBar={hideFilterBar}
    />
  );
};
